import { useEffect, useRef, useState } from "react";
import { Banknote, CreditCard, QrCode, Wallet, Landmark, Handshake, BookOpen, MoreHorizontal, Trash2, Loader2, Printer, Mail, MessageCircle, Receipt, Utensils, ShoppingBasket, Ticket } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api, brl, PAY_LABELS, ORIGIN_LABELS, fmtDateTime, errMsg, waLink, imgUrl, activeMethods } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RewardPicker } from "@/components/loyalty/RewardPicker";
import { rewardSummary } from "@/lib/loyalty";
import { MoneyInput, parseMoney } from "@/components/MoneyInput";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { fiscal } from "@/fiscal";
import { DanfeDialog } from "@/components/DanfeDialog";

const METHODS = [
  { id: "dinheiro", icon: Banknote },
  { id: "pix", icon: QrCode },
  { id: "credito", icon: CreditCard },
  { id: "debito", icon: Wallet },
  { id: "vale_refeicao", icon: Utensils },
  { id: "vale_alimentacao", icon: ShoppingBasket },
  { id: "voucher", icon: Ticket },
  { id: "transferencia", icon: Landmark },
  { id: "convenio", icon: Handshake },
  { id: "fiado", icon: BookOpen },
  { id: "outros", icon: MoreHorizontal },
];

export function PaymentDialog({ open, onOpenChange, subtotal, serviceFeePct, allowDiscount = true, allowSurcharge = false, title = "Pagamento", onConfirm, loyalty = null, methods = null, nfce = false }) {
  const [payments, setPayments] = useState([]);
  const [amount, setAmount] = useState("");
  const [discount, setDiscount] = useState("");
  const [fee, setFee] = useState(false);
  const [surcharge, setSurcharge] = useState("");
  const [loy, setLoy] = useState("");
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);
  const [cash, setCash] = useState(false);
  const [emitNfce, setEmitNfce] = useState(false);
  const amountRef = useRef(null);
  const { session } = useAuth();
  const active = activeMethods(session);
  useEffect(() => {
    if (open) {
      setPayments([]);
      setDiscount("");
      setFee(false);
      setSurcharge("");
      setAmount("");
      setLoy("");
      setSel([]);
      setCash(false);
      setEmitNfce(false);
    }
  }, [open]);

  const disc = Math.min(Number(discount) || 0, subtotal);
  const points = loyalty?.mode === "points";
  const rs = rewardSummary(points ? loyalty : null, sel);
  const loyDisc = !loyalty ? 0 : Math.max(0, points ? Math.min(rs.discount, subtotal - disc) : Math.min(Number(loy) || 0, loyalty.balance, subtotal - disc));
  const feeValue = fee && serviceFeePct ? Math.round(subtotal * serviceFeePct) / 100 : 0;
  const sur = Math.max(0, Number(surcharge) || 0);
  const total = Math.round((subtotal - disc - loyDisc + feeValue + sur) * 100) / 100;
  const paid = payments.reduce((s, p) => s + p.amount, 0);
  const remaining = Math.max(0, Math.round((total - paid) * 100) / 100);
  const change = Math.max(0, Math.round((paid - total) * 100) / 100);

  const add = (method) => {
    const val = amount === "" ? remaining : parseMoney(amount);
    setCash(false);
    if (!val || val <= 0) return;
    setPayments((p) => [...p, { method, amount: Math.round(val * 100) / 100 }]);
    setAmount("");
  };
  // Dinheiro com o campo vazio: foca o valor para o operador digitar quanto recebeu.
  const pick = (method) => {
    if (method === "dinheiro" && !cash && amount === "") {
      setCash(true);
      amountRef.current?.focus();
      return;
    }
    add(method);
  };

  const confirm = async () => {
    setBusy(true);
    try {
      await onConfirm({ payments, discount: disc, surcharge: sur, apply_service_fee: fee, loyalty_discount: points ? 0 : Math.round(loyDisc * 100) / 100,
        loyalty_rewards: points ? sel : [], emit_nfce: nfce && emitNfce });
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const canFinish = remaining === 0 && (total === 0 || payments.length > 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[94dvh] flex flex-col gap-3 p-5" data-testid="payment-modal">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>Adicione uma ou mais formas de pagamento.</DialogDescription>
        </DialogHeader>
        {/* só o meio rola: o botão Finalizar fica sempre visível, mesmo com zoom ou tela baixa */}
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain px-1 -mx-1">
          <div className="grid grid-cols-2 gap-3">
            {allowDiscount && (
              <label className="text-sm space-y-1">
                <span className="text-muted-foreground">Desconto (R$)</span>
                <Input type="number" min="0" step="0.01" value={discount} onChange={(e) => setDiscount(e.target.value)} data-testid="payment-discount-input" />
              </label>
            )}
            {allowSurcharge && (
              <label className="text-sm space-y-1">
                <span className="text-muted-foreground">Acréscimo (R$)</span>
                <Input type="number" min="0" step="0.01" value={surcharge} onChange={(e) => setSurcharge(e.target.value)} data-testid="payment-surcharge-input" />
              </label>
            )}
            {serviceFeePct > 0 && (
              <label className="flex items-center justify-between gap-2 rounded-lg border px-3 text-sm">
                <span>Taxa de serviço {serviceFeePct}%</span>
                <Switch checked={fee} onCheckedChange={setFee} data-testid="payment-service-fee-switch" />
              </label>
            )}
          </div>
          {points && <RewardPicker wallet={loyalty} sel={sel} setSel={setSel} />}
          {loyalty && !points && loyalty.balance > 0 && (
            <label className="flex items-center justify-between gap-3 rounded-lg border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-sm" data-testid="payment-loyalty-box">
              <span>Usar cashback <span className="text-xs text-muted-foreground">(disponível {brl(loyalty.balance)})</span></span>
              <Input type="number" min="0" step="0.01" value={loy} onChange={(e) => setLoy(e.target.value)} placeholder="R$" className="h-9 w-28" data-testid="payment-loyalty-input" />
            </label>
          )}
          <div className="rounded-xl bg-muted/60 px-4 py-3 space-y-1 text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span className="font-mono">{brl(subtotal)}</span></div>
            {disc > 0 && <div className="flex justify-between text-red-500"><span>Desconto</span><span className="font-mono">- {brl(disc)}</span></div>}
            {loyDisc > 0 && <div className="flex justify-between text-amber-500"><span>{points ? "Desconto com pontos" : "Cashback"}</span><span className="font-mono" data-testid="payment-loyalty-off">- {brl(loyDisc)}</span></div>}
            {rs.products.map((r) => <div key={r.id} className="flex justify-between text-amber-500"><span>Brinde: {r.name}</span><span className="font-mono">grátis</span></div>)}
            {feeValue > 0 && <div className="flex justify-between"><span>Taxa de serviço</span><span className="font-mono">{brl(feeValue)}</span></div>}
            {sur > 0 && <div className="flex justify-between"><span>Acréscimo</span><span className="font-mono">{brl(sur)}</span></div>}
            <div className="flex justify-between pt-1 border-t font-display text-lg font-bold"><span>Total</span><span data-testid="payment-total">{brl(total)}</span></div>
          </div>
          <div>
            <label htmlFor="payment-amount" className="text-sm text-muted-foreground" data-testid="payment-amount-label">
              {cash ? "Valor recebido em dinheiro" : "Valor a lançar"} (vazio = restante {brl(remaining)})
            </label>
            <div className="mt-1 flex gap-2">
              <MoneyInput id="payment-amount" ref={amountRef} value={amount} onChange={setAmount} placeholder="Digite o valor"
                onKeyDown={(e) => { if (e.key === "Enter" && cash) { e.preventDefault(); add("dinheiro"); } }}
                className={cn("h-11 flex-1 font-mono text-lg", cash && "ring-2 ring-emerald-500")} data-testid="payment-modal-cash-input" />
              {cash && <Button onClick={() => add("dinheiro")} className="h-11 bg-emerald-600 text-white hover:bg-emerald-700" data-testid="payment-cash-confirm-btn"><Banknote className="h-4 w-4 mr-2" />Lançar dinheiro</Button>}
            </div>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {METHODS.filter((m) => active.includes(m.id) && (!methods || methods.includes(m.id))).map(({ id, icon: Icon }) => (
              <Button key={id} variant="outline" className={cn("h-14 flex-col gap-1 text-xs", cash && id === "dinheiro" && "border-emerald-500 bg-emerald-500/10")} onClick={() => pick(id)} data-testid={`payment-modal-${id}-btn`}>
                <Icon className="h-5 w-5" /> {id === "credito" ? "Crédito" : id === "debito" ? "Débito" : PAY_LABELS[id]}
              </Button>
            ))}
          </div>
          {payments.length > 0 && (
            <ul className="space-y-1" data-testid="payment-list">
              {payments.map((p, i) => (
                <li key={i} className="flex items-center justify-between rounded-lg border px-3 py-2 text-sm">
                  <span>{PAY_LABELS[p.method]}</span>
                  <span className="flex items-center gap-2 font-mono">
                    {brl(p.amount)}
                    <button onClick={() => setPayments((x) => x.filter((_, j) => j !== i))} data-testid={`payment-remove-${i}`}><Trash2 className="h-4 w-4 text-muted-foreground" /></button>
                  </span>
                </li>
              ))}
            </ul>
          )}
          <div className="flex justify-between text-sm">
            <span>Restante: <b className="font-mono" data-testid="payment-remaining">{brl(remaining)}</b></span>
            <span>Troco: <b className="font-mono text-emerald-500" data-testid="payment-change">{brl(change)}</b></span>
          </div>
          {nfce && (
            <label className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-sm" data-testid="payment-nfce-row">
              <span>Emitir NFC-e<span className="block text-xs text-muted-foreground">Cupom fiscal emitido ao finalizar</span></span>
              <Switch checked={emitNfce} onCheckedChange={setEmitNfce} data-testid="payment-nfce-switch" />
            </label>
          )}
        </div>
        <DialogFooter className="shrink-0">
          <Button className="w-full h-12 text-base" disabled={!canFinish || busy} onClick={confirm} data-testid="finalize-sale-btn">
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Finalizar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ReceiptDialog({ sale, onClose }) {
  const { session } = useAuth();
  const [nota, setNota] = useState(null);
  const [danfe, setDanfe] = useState(null);
  const fiscalOn = session?.modules == null || session.modules.includes("fiscal");
  useEffect(() => {
    setNota(null);
    if (!sale?.id || !fiscalOn) return;
    fiscal.nfceDaVenda(sale.id).then((n) => setNota(n.status === "autorizada" ? n : null)).catch(() => {});
  }, [sale?.id, fiscalOn]);
  if (!sale) return null;
  const t = session.tenant;
  const sendEmail = async () => {
    try {
      toast.success((await api.post(`/sales/${sale.id}/send-receipt`)).data.message);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <>
    <Dialog open={!!sale} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="receipt-modal">
        <DialogHeader><DialogTitle>Venda #{sale.number} concluída</DialogTitle></DialogHeader>
        {nota && (
          <Button onClick={() => setDanfe(nota)} className="w-full" data-testid="receipt-nfce-btn">
            <Receipt className="h-4 w-4 mr-2" />Cupom fiscal (NFC-e nº {nota.numero})
          </Button>
        )}
        <div className="print-area rounded-lg bg-white p-4 font-mono text-xs text-slate-900">
          {t.settings?.logo_url && <img src={imgUrl(t.settings.logo_url)} alt="Logo" className="mx-auto mb-1 h-12 object-contain" data-testid="receipt-logo" />}
          <p className="text-center font-bold">{t.nome_fantasia}</p>
          {sale.store_name && sale.store_name !== t.nome_fantasia && <p className="text-center">{sale.store_name}</p>}
          <p className="text-center">{t.documento}</p>
          <p className="text-center mb-2">{fmtDateTime(sale.created_at)} · {ORIGIN_LABELS[sale.origin]}</p>
          <p className="border-t border-dashed border-slate-400 my-2" />
          {sale.items.map((i) => (
            <div key={i.id} className="flex justify-between gap-2"><span>{i.qty}x {i.name}</span><span>{brl(i.total)}</span></div>
          ))}
          <p className="border-t border-dashed border-slate-400 my-2" />
          {sale.discount > 0 && <div className="flex justify-between"><span>Desconto</span><span>-{brl(sale.discount)}</span></div>}
          {sale.loyalty_discount > 0 && <div className="flex justify-between"><span>Fidelidade</span><span>-{brl(sale.loyalty_discount)}</span></div>}
          {sale.customer && <p>Cliente: {sale.customer.name}</p>}
          {sale.service_fee > 0 && <div className="flex justify-between"><span>Serviço</span><span>{brl(sale.service_fee)}</span></div>}
          {sale.surcharge > 0 && <div className="flex justify-between"><span>Acréscimo</span><span>{brl(sale.surcharge)}</span></div>}
          {sale.extra_fee > 0 && <div className="flex justify-between"><span>Entrega</span><span>{brl(sale.extra_fee)}</span></div>}
          <div className="flex justify-between font-bold text-sm"><span>TOTAL</span><span>{brl(sale.total)}</span></div>
          {sale.pickup_number && <div className="my-2 border-2 border-slate-900 py-1 text-center" data-testid="receipt-pickup-number"><p className="text-[10px]">SENHA DE RETIRADA</p><p className="text-2xl font-extrabold">{String(sale.pickup_number).padStart(3, "0")}</p></div>}
          {sale.payments.map((p, i) => <div key={i} className="flex justify-between"><span>{PAY_LABELS[p.method]}</span><span>{brl(p.amount)}</span></div>)}
          {sale.change > 0 && <div className="flex justify-between"><span>Troco</span><span>{brl(sale.change)}</span></div>}
          {sale.notes && <p className="mt-2">Obs.: {sale.notes}</p>}
          {sale.loyalty_earned > 0 && <p className="mt-2">Fidelidade acumulada: {sale.loyalty_earned}</p>}
          <p className="mt-2">Operador: {sale.user_name}</p>
          {sale.receipt_footer && <p className="text-center mt-3 whitespace-pre-line" data-testid="receipt-footer">{sale.receipt_footer}</p>}
          <p className="text-center mt-3">Documento sem valor fiscal</p>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Button variant="outline" disabled={!sale.customer?.email} onClick={sendEmail} data-testid="receipt-email-btn"><Mail className="h-4 w-4 mr-2" />E-mail</Button>
          <Button asChild variant="outline" data-testid="receipt-whatsapp-btn">
            <a href={waLink(sale.customer?.phone, receiptText(sale, t))} target="_blank" rel="noreferrer"><MessageCircle className="h-4 w-4 mr-2" />WhatsApp</a>
          </Button>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => window.print()} data-testid="receipt-print-btn"><Printer className="h-4 w-4 mr-2" />Imprimir</Button>
          <Button onClick={onClose} data-testid="receipt-close-btn">Nova venda</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <DanfeDialog nota={danfe} onClose={() => setDanfe(null)} />
    </>
  );
}

const receiptText = (sale, t) =>
  [`${t.nome_fantasia} - Comprovante #${sale.number}`, fmtDateTime(sale.created_at), ...sale.items.map((i) => `${i.qty}x ${i.name} ${brl(i.total)}`),
    `Total: ${brl(sale.total)}`, sale.payments.map((p) => `${PAY_LABELS[p.method]} ${brl(p.amount)}`).join(" + "), "Documento sem valor fiscal"].join("\n");
